/** Menu option groups (QSR) — distinct from clothing product_variants. */

/** Semántica explícita del modifier; ausente = heurística legacy por texto/precio. */
export type MenuModifierKind = 'extra' | 'exclude' | 'detail';

export type MenuOptionChoice = {
  id: string;
  name: string;
  priceDelta: number;
  maxQty: number;
  linkedProductId?: string | null;
  isAvailable: boolean;
  sortOrder: number;
  modifierKind?: MenuModifierKind | null;
  /** Alérgenos que aporta la opción (mismo vocabulario que products.allergens). */
  allergenTags?: string[];
  /** Copy corto para comanda/KDS; vacío = usa name. */
  kitchenLabel?: string | null;
  /** Opción del mismo grupo que esta opción reemplaza. */
  substitutesChoiceId?: string | null;
  /** Componente de combo (producto) que esta opción reemplaza al vender un combo. */
  substitutesProductId?: string | null;
};

export type MenuOptionGroup = {
  id: string;
  name: string;
  selectionMin: number;
  selectionMax: number;
  sortOrder: number;
  choices: MenuOptionChoice[];
};

export type SelectedMenuOption = {
  groupId: string;
  groupName: string;
  choiceId: string;
  choiceName: string;
  priceDelta: number;
  qty: number;
  linkedProductId?: string | null;
  modifierKind?: MenuModifierKind | null;
  allergenTags?: string[];
  kitchenLabel?: string | null;
  substitutesChoiceId?: string | null;
  substitutesProductId?: string | null;
};

export function optionsPriceDelta(options: SelectedMenuOption[] | null | undefined): number {
  if (!options?.length) return 0;
  return options.reduce((sum, o) => sum + o.priceDelta * Math.max(1, o.qty), 0);
}

export function formatOptionsLabel(options: SelectedMenuOption[] | null | undefined): string {
  if (!options?.length) return '';
  return options
    .map((o) => (o.qty > 1 ? `${o.choiceName}×${o.qty}` : o.choiceName))
    .join(', ');
}

/** Stable key for cart merge (productId + this + lineNote). */
export function menuOptionsKey(options: SelectedMenuOption[] | null | undefined): string {
  if (!options?.length) return '';
  return JSON.stringify(options.map((o) => `${o.choiceId}:${o.qty}`).sort());
}

const IMPERATIVE_GROUP_PREFIX = /^(sumá|elegí|agregá|escogé)\s+/i;

/** Label for the explicit “none” row on optional single-select groups. */
export function noneChoiceLabel(groupName: string): string {
  let n = groupName.trim();
  if (!n) return 'Ninguno';
  n = n.replace(IMPERATIVE_GROUP_PREFIX, '');
  if (/^sin\s/i.test(n)) return 'Ninguno';
  return `Sin ${n.charAt(0).toLowerCase()}${n.slice(1)}`;
}

/** Optional multi-select: empty selection is enough — no “Sin …” row. */
export function shouldShowNoneChoiceRow(group: Pick<MenuOptionGroup, 'selectionMin' | 'selectionMax'>): boolean {
  return group.selectionMin === 0 && group.selectionMax === 1;
}

/** Required group with every choice unavailable — cannot complete selection. */
export function isMenuGroupFullyUnavailable(group: MenuOptionGroup): boolean {
  return group.selectionMin > 0 && !group.choices.some((c) => c.isAvailable);
}

export function unitPriceWithOptions(basePrice: number, options: SelectedMenuOption[] | null | undefined): number {
  return Math.max(0, basePrice + optionsPriceDelta(options));
}

export function parseModifierKind(raw: unknown): MenuModifierKind | null {
  return raw === 'extra' || raw === 'exclude' || raw === 'detail' ? raw : null;
}

/** Snapshot de la elección: lleva la metadata explícita al carrito/venta/cocina. */
export function toSelectedMenuOption(
  group: MenuOptionGroup,
  choice: MenuOptionChoice,
  qty = 1,
): SelectedMenuOption {
  return {
    groupId: group.id,
    groupName: group.name,
    choiceId: choice.id,
    choiceName: choice.name,
    priceDelta: choice.priceDelta,
    qty,
    linkedProductId: choice.linkedProductId ?? null,
    modifierKind: choice.modifierKind ?? null,
    allergenTags: choice.allergenTags ?? [],
    kitchenLabel: choice.kitchenLabel ?? null,
    substitutesChoiceId: choice.substitutesChoiceId ?? null,
    substitutesProductId: choice.substitutesProductId ?? null,
  };
}

/** Group flat RPC rows into per-product group lists. */
export function groupMenuOptionRows(
  rows: Array<{
    product_id: string;
    group_id: string;
    group_name: string;
    selection_min: number;
    selection_max: number;
    group_sort: number;
    choice_id: string;
    choice_name: string;
    price_delta: number;
    max_qty: number;
    linked_product_id?: string | null;
    is_available: boolean;
    choice_sort: number;
    modifier_kind?: string | null;
    allergen_tags?: string[] | null;
    kitchen_label?: string | null;
    substitutes_choice_id?: string | null;
    substitutes_product_id?: string | null;
  }>,
): Map<string, MenuOptionGroup[]> {
  const byProduct = new Map<string, Map<string, MenuOptionGroup>>();
  for (const row of rows) {
    let groups = byProduct.get(row.product_id);
    if (!groups) {
      groups = new Map();
      byProduct.set(row.product_id, groups);
    }
    let group = groups.get(row.group_id);
    if (!group) {
      group = {
        id: row.group_id,
        name: row.group_name,
        selectionMin: row.selection_min,
        selectionMax: row.selection_max,
        sortOrder: row.group_sort,
        choices: [],
      };
      groups.set(row.group_id, group);
    }
    group.choices.push({
      id: row.choice_id,
      name: row.choice_name,
      priceDelta: Number(row.price_delta) || 0,
      maxQty: row.max_qty,
      linkedProductId: row.linked_product_id ?? null,
      isAvailable: row.is_available !== false,
      sortOrder: row.choice_sort,
      modifierKind: parseModifierKind(row.modifier_kind),
      allergenTags: Array.isArray(row.allergen_tags) ? row.allergen_tags.filter(Boolean) : [],
      kitchenLabel: row.kitchen_label ?? null,
      substitutesChoiceId: row.substitutes_choice_id ?? null,
      substitutesProductId: row.substitutes_product_id ?? null,
    });
  }
  const result = new Map<string, MenuOptionGroup[]>();
  for (const [productId, groups] of byProduct) {
    const list = [...groups.values()].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const g of list) {
      g.choices.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    result.set(productId, list);
  }
  return result;
}

export function validateMenuSelection(
  groups: MenuOptionGroup[],
  selected: SelectedMenuOption[],
): string | null {
  for (const group of groups) {
    const picks = selected.filter((s) => s.groupId === group.id);
    const totalQty = picks.reduce((n, p) => n + p.qty, 0);
    if (totalQty < group.selectionMin) {
      return `Elegí al menos ${group.selectionMin} en «${group.name}»`;
    }
    if (totalQty > group.selectionMax) {
      return `Máximo ${group.selectionMax} en «${group.name}»`;
    }
    for (const pick of picks) {
      const choice = group.choices.find((c) => c.id === pick.choiceId);
      if (!choice || !choice.isAvailable) {
        return `Opción agotada en «${group.name}»`;
      }
      if (pick.qty > choice.maxQty) {
        return `Máximo ${choice.maxQty} de ${choice.name}`;
      }
    }
  }
  return null;
}
